// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import cstmTable from '../../../../../share/components/vue/PaginationTable';
import alert from '../../../../../share/alert';
import config from "../../../../../share/config";
import loading from '../../../../../share/components/vue/ProcessingIcon';
import apiService from '../../../../../share/util/api/service';
import { executeSyncRequest } from '../../../../../share/util/api/syncRequest';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import obdOnboardSystemTestExecute from '../../../../vue/obd/individual/execute/ObdOnboardSystemTestExecute';
import { pushFunctionList } from '../../../../../router/obd';
import { OBD_OST } from '../../../../../constants/usageCountTypes.js';
import diagnoseResource from "../../../../../share/util/diagnose";

// テスト項目定義
const systemTestMenuList = require('../../../../../data/obd/onboard_system_test_menu_list.json');

const target = "ost";

export default {
  name: "obdOnboardSystemTest",
  data() {
    return {
      processing: false,
      currentWizard: '',
      // 機能リスト
      originalFunctionList: [],
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      // 選択中の機能オブジェクト
      selectedFunction: {},
      // フィルター用テキスト
      filterText: '',
      // サポート有機能
      supportIds: [],
      executeModal: false,
    };
  },

  props: {
    // noop
  },

  components: {
    'processing-view': loading,
    'cstm-table': cstmTable,
    'execute_modal': obdOnboardSystemTestExecute
  },

  watch: {
    language: {
      immediate: true,
      handler(val) {
        val; // To avoid warning
        const MenuList = this.getObdi18nMenuList(this.supportIds);
        this.filterFunctionList = MenuList;
        this.originalFunctionList = MenuList;
        this.updateFilter();
      }
    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      obdDataId: state => state.obd.obdDataId,
    }),

    systemTableColumns() {
      return [
        { name: 'menu_title', label: 'individual.header_work_support_table_item' },
      ];
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),
    ...mapActions('observation', ['usageCountUp']),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      this.updateTable();
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * その他のエラー発生時の処理を行う
     * @param {string} action 処理内容
     */
    showUnexpectedError() {
      const messages = this.$t(
        "fixed_resource.obd_function_common_message_result_ng_2"
      );
      alert.showError(this, null, messages, () => {
        // 遷移処理
        pushFunctionList(this, this.$route.params.systemNo);
      });
    },

    /**
     * 選択した機能を変数に保持
     */
    clickRow: function (selected) {
      this.selectedFunction = selected;
      this.setHighLight();
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.filterFunctionList.forEach(item => {
        if (item.id === this.selectedFunction.id) {
          item.fieldSpec = {};
          item.fieldSpec.menu_title = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
          }
        }
      });
    },

    /**
     * 選択状態を解除し、フィルターの文字列に応じてリストをフィルターする
     */
    updateFilter() {
      this.selectedFunction = {};
      this.setHighLight();
      this.filterFunctionList = this.originalFunctionList.filter(
        // 翻訳後の名称を大文字・小文字区別なくフィルターする
        item => item.menu_title.toLowerCase().includes(this.filterText.toLowerCase())
      );
    },

    /** サポート機能選択時 */
    onClickSelect() {
      // 選択中の機能がない場合、return
      if (!this.selectedFunction.menu_title) {
        return;
      }

      this.originalFunctionList.forEach(element => {
        if (this.selectedFunction.menu_title === element.menu_title) {
          this.executeModal = true;
        }
      });

      // OBDシステム利用回数カウントアップ
      this.usageCountUp({ type: OBD_OST });
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.executeModal = false;
    },

    /**
     * テーブル情報の更新を行う
     */
    async updateTable() {
      try {
        this.processing = true;
        // オンボードシステムテスト機能一覧の取得
        const response = await executeSyncRequest(
          this,
          async () => {
            return await apiService.getCall(
              apiService.OBD, 
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/supportList/${target}`,
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.supportIds = []; },
          // GET API で正常レスポンスが返ってきた際の内容チェックはデフォルト処理を利用
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する
          (result) => { this.validateErrorResult(result); },
        );
        if (response && response.data.support_ids !== null) {
          // 作業サポート一覧の多言語変換リストの取得
          this.supportIds = response.data.support_ids;
          const MenuList = this.getObdi18nMenuList(this.supportIds);
          this.filterFunctionList = MenuList;
          this.originalFunctionList = MenuList;
        } else {
          this.supportIds = [];
        }
        this.updateFilter();  
      } finally {
        this.processing = false;
      }
    },

    /**
     * 対象テスト項目の多言語変換の一覧取得
     * @param {Array} supportIds 
     */
    getObdi18nMenuList(supportIds) {
      /**
       * 翻訳キーとメッセージキーを元に文言が存在すれば、その翻訳後の文言を返す。
       * ない場合は、defaultMessage を返す（defaultMessage 未指定の場合は空文字を返す）
       * @param {string} i18nWsMenuKey 翻訳キー
       * @param {string} messageKey メッセージキー
       * @returns {string | undefined} 翻訳後の文言（見つからなかった場合は undefined）
       */
      const convertI18nText = (i18nObdMenuKey, messageKey) => {
        const key = `${i18nObdMenuKey}.${messageKey}`;
        return this.$te(key) ? this.$t(key) : undefined;
      };

      //多言語変換リストの取得
      return supportIds.map(value =>{
        const i18nObdMenuKey = `fixed_resource`;
        const messageKey = systemTestMenuList[`${value}`];
        return {
          id: value,
          menu_title: convertI18nText(i18nObdMenuKey, messageKey["menu"]),
          menu_description: convertI18nText(i18nObdMenuKey, messageKey["description"]) || '',
        };
      });
    },
  },

  mounted() {
    this.mountedLogic();
  }
};